import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Form, InputNumber, Radio } from 'antd';
import { useContext } from 'react';

import { CurrencyContext } from 'app/components/commons/Currency/CurrencyContext/CurrencyContext';
import { FormLineLayout } from 'app/components/commons/Form/FormLineLayout/FormLineLayout';
import { PAX_TYPE } from 'app/typings/core/admin/enum/paxType';
import {
  PRICE_COST_TYPE,
  PRICE_DIFFERENTIATION_MODE,
} from 'app/typings/core/common/openingConfiguration/priceConfigurationType';

import type { BuilderPriceFormValues } from '../types';

import styles from './Price.module.scss';

const RULES = [{ required: true, message: 'Set a valid price value' }];

const PRICE_FIELD = [
  'openingConfiguration',
  'priceConfiguration',
  'configuration',
  'price',
];
const CHILD_PRICE_FIELD = [
  'openingConfiguration',
  'priceConfiguration',
  'configuration',
  'childPrice',
];
const MODE_FIELD = [
  'openingConfiguration',
  'priceConfiguration',
  'priceDifferentiationMode',
];

export const Price = () => {
  const form = Form.useFormInstance();
  const { currencySymbol } = useContext(CurrencyContext);

  const priceConfiguration: BuilderPriceFormValues | undefined = Form.useWatch(
    ['openingConfiguration', 'priceConfiguration'],
    form
  );
  const allowedPaxTypes: string[] | undefined = Form.useWatch(
    ['allowedPaxTypes'],
    form
  );

  const hasFees = priceConfiguration?.cost === PRICE_COST_TYPE.PAID;
  const hasChildPrice =
    priceConfiguration?.priceDifferentiationMode ===
    PRICE_DIFFERENTIATION_MODE.WITH_CHILD;

  const hasAdultOnly =
    allowedPaxTypes?.length === 1 && allowedPaxTypes[0] === PAX_TYPE.ADULT;
  const hasChildOnly =
    allowedPaxTypes?.length === 1 && allowedPaxTypes[0] === PAX_TYPE.CHILD;
  const hasBothPaxTypes = !hasAdultOnly && !hasChildOnly;

  const toggleChildPrice = () => {
    form.setFieldValue(
      MODE_FIELD,
      hasChildPrice
        ? PRICE_DIFFERENTIATION_MODE.NONE
        : PRICE_DIFFERENTIATION_MODE.WITH_CHILD
    );
  };

  const renderPriceField = (name: string[], label: string) => (
    <FormLineLayout
      key={name.join('.')}
      label={label}
      isRequired={label.trim().length > 0}
    >
      <div className={styles.priceInput}>
        <Form.Item name={name} rules={RULES}>
          <InputNumber type="number" addonAfter={currencySymbol} min={0} />
        </Form.Item>
        <span className={styles.priceUnit}>/unit</span>
      </div>
    </FormLineLayout>
  );

  const renderPriceFields = () => {
    if (hasChildOnly) {
      return renderPriceField(CHILD_PRICE_FIELD, 'Child price');
    }

    if (!hasChildPrice) {
      return renderPriceField(PRICE_FIELD, '');
    }

    return (
      <>
        {renderPriceField(PRICE_FIELD, 'Adult price')}
        {renderPriceField(CHILD_PRICE_FIELD, 'Child price')}
      </>
    );
  };

  return (
    <>
      <Form.Item name={MODE_FIELD} hidden>
        <input type="hidden" />
      </Form.Item>

      <FormLineLayout label="Cost" isRequired>
        <Form.Item
          name={['openingConfiguration', 'priceConfiguration', 'cost']}
        >
          <Radio.Group>
            <Radio value={PRICE_COST_TYPE.FREE}>Free of charge</Radio>
            <Radio value={PRICE_COST_TYPE.PAID}>Applicable fees</Radio>
          </Radio.Group>
        </Form.Item>
      </FormLineLayout>

      {hasFees && (
        <>
          <div className={styles.priceConfiguration}>{renderPriceFields()}</div>
          {hasBothPaxTypes && (
            <FormLineLayout>
              <Button
                icon={hasChildPrice ? <DeleteOutlined /> : <PlusOutlined />}
                onClick={toggleChildPrice}
                type={hasChildPrice ? 'dashed' : 'default'}
                danger={hasChildPrice}
              >
                {`${hasChildPrice ? 'Delete' : 'Add'} adult/child price`}
              </Button>
            </FormLineLayout>
          )}
        </>
      )}
    </>
  );
};
